/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-key */
import React, { useEffect, useState } from 'react';
import head from 'lodash/head';
import debounce from 'lodash/debounce';
import { PropTypes } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import CustomTextArea from 'Common/Components/CustomTextArea';
import UserSelector from './components/UserSelector';
import {
  createUpdateWorkspace,
  resetCreationState,
  setField,
  getUsers,
  seedCurrentUserAsAssignee,
  seedAssigneesForEdit
} from 'Screens/Contextualization/Workspaces/actions/Actions';
import { FiTrash2 as Trash } from 'react-icons/fi';
import {
  selectWsEditCreateName,
  selectWsEditCreateDescription,
  selectWsEditCreateStart,
  selectWsEditCreateEnd,
  selectWsEditCreateLocale,
  selectWsEditCreateId,
  selectWorkspaceSaved,
  selectIsFetching,
  selectScope,
  selectWsEditCreateMakePublic,
  selectWsEditCreateCurrentUsers,
  selectWsEditCreateImportance,
  selectWsEditCustomer,
  selectWsEditGroupBy,
  selectWsEditGroupByMatchPrecision,
  selectWsEditGroupByAlgorithm,
  selectWsEditRiskScoreProfile
} from 'store/WorkspaceEditCreate/selectors';
import { EEFeatureSlot } from 'Common/ee/EEFeatureSlot';
import ModalWrapper from 'Common/Components/ModalWrapper';
import CustomList from 'Common/Components/CustomList';
import {
  Modal,
  Title,
  Header,
  CancelButton,
  CreateButton,
  Body,
  FieldTitle,
  Field,
  FieldDescription,
  SecondTitle,
  SubTitle,
  UserSelectorWrapper,
  UserSelect,
  TextAreaContainer,
  FieldTitleDescription,
  UserRow,
  NameRow,
  RoleRow,
  IconRow,
  TopRow,
  FieldScope,
  TextField,
  TextSpan,
  Flex,
  FieldExplore,
  FieldScopeColumn
} from './styled';
import { getEEFeature, getEESelector } from 'Common/ee/registry';
import { selectUserName, selectUserRole } from 'store/Faraday/selectors';
import Importance from 'Common/Components/Importance';
import {
  FEATURE_WORKSPACES,
  PERMISSION_TYPES
} from 'Hooks/usePermission/permissions';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { selectLocation } from 'store/Router/selectors';
import DateRangePicker from 'Common/Components/Contextualization/DateRangePicket';
import Checkbox from 'Common/Components/Checkbox';
import isEmpty from 'lodash/isEmpty';
import CreationWarning from 'Common/Components/CreationWarning';
import useModalShortcuts from 'Hooks/useModalShortcuts';

const WS_NAME_REGEX = /^[A-Za-z0-9_-]+$/;

const ModalCreation = ({ handleClose }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const name = useSelector(selectWsEditCreateName);
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectUserName);
  const isWorkspaceAdmin = userRole === 'workspace_admin';

  const hasWorkspaceUsersFeature = !!getEESelector('workspace-users', null);
  const hasGroupByControl = !!getEEFeature('workspace-group-by-control');
  const users = useSelector(getEESelector('workspace-users', () => []));
  const activeUsers = users.filter(({ active }) => active);
  const currentUsers = useSelector(selectWsEditCreateCurrentUsers);
  const addedUsers = activeUsers.filter(({ username }) =>
    currentUsers.includes(username)
  );
  const mappedAddedUsers = currentUsers.map((username) =>
    addedUsers.find((user) => user.username === username)
  );
  const filteredMappedAddedUsers = isWorkspaceAdmin
    ? currentUsers.map((username) => ({ username, roles: username === currentUser ? [userRole] : [] }))
    : mappedAddedUsers.filter((user) => user !== undefined);
  const notAddedUsers = activeUsers.filter(
    ({ username, roles }) =>
      !currentUsers.includes(username) && head(roles) !== 'admin'
  );
  const customer = useSelector(selectWsEditCustomer);
  const groupby = useSelector(selectWsEditGroupBy);
  const matchPrecision = useSelector(selectWsEditGroupByMatchPrecision);
  const groupAlgorithm = useSelector(selectWsEditGroupByAlgorithm);
  const riskScoreProfile = useSelector(selectWsEditRiskScoreProfile);
  // const publicWS = useSelector(selectWsEditCreatePublic);
  const description = useSelector(selectWsEditCreateDescription);
  const start = useSelector(selectWsEditCreateStart);
  const end = useSelector(selectWsEditCreateEnd);
  const locale = useSelector(selectWsEditCreateLocale);
  const id = useSelector(selectWsEditCreateId);
  const makePublic = useSelector(selectWsEditCreateMakePublic);
  const workspaceSaved = useSelector(selectWorkspaceSaved);
  const isFetching = useSelector(selectIsFetching);

  const scope = useSelector(selectScope);
  const importance = useSelector(selectWsEditCreateImportance);

  const location = useSelector(selectLocation);

  const [isValidWsName, setIsValidWsName] = useState(true);
  const [exploreWs, setExploreWs] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const createWarningTitle = intl.formatMessage({
    id: 'workspaces.modal.create.title'
  });
  const createWarningMessage = intl.formatMessage({
    id: 'modal.createVuln.cantCreate.message'
  });
  const nameField = intl.formatMessage({
    id: 'workspaces.modal.newWorkspace.name'
  });
  const exploreWsLabel = intl.formatMessage({
    id: 'workspaces.modal.newWorkspace.exploreWs'
  });

  useEffect(() => () => dispatch(resetCreationState()), [dispatch]);

  useEffect(() => {
    if (workspaceSaved) handleClose();
  }, [handleClose, workspaceSaved]);

  useEffect(() => {
    if (isWorkspaceAdmin) return;
    if (hasWorkspaceUsersFeature && location.pathname !== '/workspaces') dispatch(getUsers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRemove = (roles) => head(roles) !== 'admin' && !isWorkspaceAdmin;

  const validateWsName = (value) => {
    if (!value) return setIsValidWsName(true);

    setIsValidWsName(WS_NAME_REGEX.test(value));
  };

  const handleChange = (field, value) => {
    dispatch(setField(field, value));

    if (field === 'name') validateWsName(value);
  };

  const clampPrecision = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return 10;
    return Math.min(100, Math.max(10, n));
  };

  const handleGroupByChange = (value) => {
    handleChange('groupby', value);

    if (value === 'name') {
      if (!groupAlgorithm) handleChange('groupAlgorithm', 'levenshtein');
      if (!matchPrecision) handleChange('matchPrecision', 80);
      return;
    }

    handleChange('matchPrecision', 80);
    handleChange('groupAlgorithm', 'levenshtein');
  };

  const handleMatchPrecisionChange = (value) => {
    handleChange('matchPrecision', clampPrecision(value));
  };

  const onChangeDebounce = debounce(handleChange, 250);

  const onCloseCalendar = (startDate, endDate) => {
    dispatch(setField('start', new Date(startDate)));
    dispatch(setField('end', new Date(endDate)));
  };

  const cleanDates = () => {
    dispatch(setField('start', ''));
    dispatch(setField('end', ''));
  };

  const onAddScope = (value) => dispatch(setField('scope', [...scope, value]));

  const onRemoveScope = (indexToRemove) => {
    const newScope = scope.filter((item, index) => index !== indexToRemove);
    dispatch(setField('scope', newScope));
  };

  useEffect(() => {
    if (!id) dispatch(seedCurrentUserAsAssignee());
    else if (name) dispatch(seedAssigneesForEdit(name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  const addUser = (username) =>
    dispatch(setField('users', [...currentUsers, username]));

  const removeUser = (username) =>
    dispatch(
      setField(
        'users',
        currentUsers.filter((usr) => usr !== username)
      )
    );

  const canUpdate = useSelector((state) =>
    selectAllowedByRole(state, FEATURE_WORKSPACES, PERMISSION_TYPES.CREATE)
  );

  const enabledCreate = !isEmpty(name) && isValidWsName;

  const handleCreateWs = () => {
    validateWsName(name);

    if (isValidWsName) {
      if (enabledCreate) dispatch(createUpdateWorkspace(exploreWs));
      else setShowErrorModal(true);
    }
  };

  useModalShortcuts({
    id: 'workspace-create',
    onSave: handleCreateWs,
    onCancel: handleClose,
    canSave: enabledCreate,
    saveLabel: id ? intl.formatMessage({ id: 'save' }) : intl.formatMessage({ id: 'create' })
  });

  return (
    <ModalWrapper>
      <Modal>
        <Header>
          <Title>
            <FormattedMessage id={ `workspaces.modal.${id ? 'edit' : 'create'}.title` } />
          </Title>
          <CancelButton onClick={ handleClose } id="modal-ws-cancel-btn">
            <FormattedMessage id="cancel" />
          </CancelButton>
          <CreateButton
            id="modal-ws-accept-btn"
            disabled={ !enabledCreate }
            onClick={ handleCreateWs }
            isLoading={ isFetching }
          >
            { id
              ? intl.formatMessage({ id: 'save' })
              : intl.formatMessage({ id: 'create' }) }
          </CreateButton>
        </Header>
        <Body id="modalBody">
          <TopRow>
            <TextField
              title={ nameField }
              name="name"
              placeholder="New Workspace"
              value={ name }
              onChange={ (value) => handleChange('name', value) }
              error={ !isValidWsName }
              required
              errorMessage="No spaces and no special chars (except _ and -) are allowed"
            />
            <Field index={ 1 } id="dateSection">
              <FieldTitle
                children={
                  <FormattedMessage id="workspaces.modal.newWorkspace.StartAndEndDate" />
                }
              />
              <DateRangePicker
                id="modal-ws-date"
                width="100%"
                height="34px"
                start={ start }
                end={ end }
                onClose={ onCloseCalendar }
                locale={ locale }
                cleanDates={ cleanDates }
              />
            </Field>
          </TopRow>

          <FieldScope>
            <TextField
              fullwidth={ !hasGroupByControl }
              title="Customer"
              name="customer"
              placeholder="Customer Name"
              value={ customer }
              onChange={ (value) => handleChange('customer', value) }
            />
            <EEFeatureSlot
              featureKey="workspace-group-by-control"
              render={ (GroupByWithMatchPrecision) => (
                <div style={ { width: '50%' } }>
                  <FieldTitle>Group By</FieldTitle>
                  <GroupByWithMatchPrecision
                    id="modal-ws-group-by"
                    value={ groupby }
                    matchPrecision={ matchPrecision }
                    onChange={ handleGroupByChange }
                    onMatchPrecisionChange={ handleMatchPrecisionChange }
                    disabled={ isFetching }
                  />
                </div>
              ) }
              fallback={ null }
            />
          </FieldScope>

          <FieldDescription id="descriptionSection">
            <FieldTitleDescription>
              <FormattedMessage id="workspaces.modal.newWorkspace.description" />
            </FieldTitleDescription>
            <TextAreaContainer>
              <CustomTextArea
                id="modal-ws-description"
                width="100%"
                height="55px"
                title=""
                placeholder={ intl.formatMessage({
                  id: 'workspaces.modal.newWorkspace.usedFor'
                }) }
                updateValue={ (value) => onChangeDebounce('description', value) }
                field=""
                defaultValue={ description }
              />
            </TextAreaContainer>
          </FieldDescription>

          { !id && (
            <EEFeatureSlot
              featureKey="workspace-create-risk-score-field"
              render={ (RiskScoreProfileCreateField) => (
                <RiskScoreProfileCreateField
                  value={ riskScoreProfile }
                  onChange={ (value) => handleChange('riskScoreProfile', value) }
                />
              ) }
              fallback={ null }
            />
          ) }

          <Flex justify="space-between" align="center">
            <Flex align="center">
              <Importance
                small
                importance={ importance }
                callback={ (value) => handleChange('importance', value) }
              />
              <TextSpan>Importance</TextSpan>
            </Flex>
            <div>
              <Checkbox
                state={ makePublic }
                onChange={ (value) =>
                  handleChange('public', !value.currentTarget.checked)
                }
              />
              <TextSpan>Make Workspace Public</TextSpan>
            </div>
          </Flex>

          <FieldScopeColumn>
            <FieldTitleDescription
              children={
                <FormattedMessage id="workspaces.modal.newWorkspace.scope" />
              }
            />
            <CustomList
              key="MODAL_WS_CREATION"
              placeholder="Scope target"
              addItem={ onAddScope }
              removeItem={ onRemoveScope }
              listData={ scope }
              canUpdate={ canUpdate }
              responsive
            />
          </FieldScopeColumn>
          {/* "Assign Users" section: org-wide multi-user assignment is an EE-only
              feature (community has no Users management at all) -- hide it here the
              same way WorkspaceDetail's equivalent section is hidden. */}
          {hasWorkspaceUsersFeature && (
            <>
              <SecondTitle
                children={
                  <FormattedMessage id="workspaces.modal.newWorkspace.secondTitle" />
                }
              />
              <SubTitle
                children={
                  <FormattedMessage id={ isWorkspaceAdmin ? 'workspaces.modal.newWorkspace.subtitle.needsWorkspace' : 'workspaces.modal.newWorkspace.subtitle' } />
                }
              />
              <SubTitle
                children={
                  <FormattedMessage id="workspaces.modal.newWorkspace.assigneesLegend" />
                }
              />
              {!isWorkspaceAdmin && (
                <UserSelectorWrapper id="userSelectorSection">
                  <UserSelector
                    data={ notAddedUsers }
                    placeholder={ intl.formatMessage({ id: 'username' }) }
                    onSelect={ addUser }
                    id="modal-ws-assign-users"
                  />
                </UserSelectorWrapper>
              )}
              <UserSelect id="userListSection">
                {filteredMappedAddedUsers.map(({ roles, username }) => (
                  <UserRow key={ `user-${username}` }>
                    <NameRow id="username" children={ username } />
                    <RoleRow
                      role={ head(roles) }
                      id="userRole"
                      children={ intl.formatMessage({ id: head(roles) }) }
                    />
                    {canRemove(roles) && (
                      <IconRow
                        onClick={ () => removeUser(username) }
                        id="userDeleteIcon"
                        children={ <Trash color="#1c4566" /> }
                      />
                    )}
                  </UserRow>
                ))}
              </UserSelect>
            </>
          )}
          <FieldExplore>
            <Checkbox
              state={ exploreWs }
              onChange={ (e) => setExploreWs(!e.currentTarget.checked) }
              id="modal-ws-explore-ws"
            />
            <TextSpan>{exploreWsLabel}</TextSpan>
          </FieldExplore>
        </Body>
      </Modal>
      {showErrorModal && (
        <CreationWarning
          errorTitle={ createWarningTitle }
          errorMessage={ createWarningMessage }
          onClose={ () => setShowErrorModal(false) }
        />
      )}
    </ModalWrapper>
  );
};

ModalCreation.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default ModalCreation;
